import { Box } from '@material-ui/core';
import { Alert } from '@mui/material';
import HighchartsReact from 'highcharts-react-official';
import highchartsMore from 'highcharts/highcharts-more.js';
import Highcharts from 'highcharts/highstock';
import xRange from 'highcharts/modules/xrange.js';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { COLORS } from '../../../utils/colors';
import { GraphResponseTimeInfo } from './GraphResponseTimeInfo';
import { useObjectivesList } from '../../../hooks/useObjectivesList';

highchartsMore(Highcharts);
xRange(Highcharts);

export function ResponseTimeConsolidated({ externalData }) {
  const chartResponseTimeConsolidatedRef = React.useRef(null);
  const chartNodeRef = React.useRef(null);
  const containerRef = React.useRef(null);
  const { t } = useTranslation();
  const [hoverData, setHoverData] = React.useState(null);
  const { data: objectives } = useObjectivesList({ fromStorage: true });

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver(() => {
      if (chartResponseTimeConsolidatedRef.current) {
        chartResponseTimeConsolidatedRef.current.chart.reflow();
      }
      if (chartNodeRef.current) {
        chartNodeRef.current.chart.reflow();
      }
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.unobserve(container);
    };
  }, []);

  function onHoverData(e) {
    const { x, y, series } = e;
    let point = [];
    point['x'] = x;
    point['y'] = y;
    let step = series.userOptions.name;
    setHoverData({ ...point, step });
  }

  function processData() {
    let arrayChartData = [];
    const colors = [
      '#2199e8',
      '#fed12d',
      '#ff7373',
      '#ccff00',
      '#b6fcd5',
      '#065535',
      '#ffc0cb',
      '#008080',
      '#00ffff',
      '#ffa500',
      '#800000',
      '#fff68f',
      '#bada55',
      '#8a2be2',
      '#3b5998',
    ];
    let sla_rendimiento_error = 0;
    let sla_rendimiento_ok = 90;
    if (objectives) {
      const currentObj = objectives.find(
        (obj) =>
          parseInt(obj.objetivo_id) === parseInt(externalData.Objetivo.Objetivo_Id)
      );
      if (currentObj) {
        sla_rendimiento_error = parseFloat(currentObj.sla_rendimiento_error);
        sla_rendimiento_ok = parseFloat(currentObj.sla_rendimiento_ok);
      }
    }
    Object.keys(externalData.Objetivo.Paso).map((key) => {
      let chartDataSeries = {};
      let arrayData = [];

      externalData.Objetivo.Paso[key].rendimientoDetalle.forEach(
        (item, forEachKey) => {
          var dict = {};
          let date = new Date(item.inicio);
          let year = date.getFullYear();
          let month = date.getMonth();
          let day = date.getDate();
          let hours = date.getHours();
          let minutes = date.getMinutes();
          let seconds = date.getSeconds();
          dict['x'] = Date.UTC(year, month, day, hours, minutes, seconds);
          dict['y'] = item.respuesta;
          arrayData.push(dict);
        }
      );
      chartDataSeries['name'] = externalData.Objetivo.Paso[key].paso_nombre;
      chartDataSeries['color'] = colors[key];
      chartDataSeries['data'] = arrayData;
      arrayChartData.push(chartDataSeries);
    });

    const arrayChartDataMarked = externalData.Objetivo.Peridos_Marcados.map(
      (item) => {
        let chartDataSeries = {};
        if (item.id_tipo_marcado === 7) {
          chartDataSeries['from'] = '';
          chartDataSeries['to'] = '';
          chartDataSeries['color'] = '';
          chartDataSeries['zIndex'] = 1;
        }
        if (item.id_tipo_marcado === 9) {
          let dateStart = new Date(item.fecha_inicio);
          let dateEnd = new Date(item.fecha_termino);
          chartDataSeries['from'] = Date.UTC(
            dateStart.getFullYear(dateStart),
            dateStart.getMonth(dateStart),
            dateStart.getDate(dateStart),
            dateStart.getHours(dateStart),
            dateStart.getMinutes(dateStart),
            dateStart.getSeconds(dateStart)
          );
          chartDataSeries['to'] = Date.UTC(
            dateEnd.getFullYear(dateEnd),
            dateEnd.getMonth(dateEnd),
            dateEnd.getDate(dateEnd),
            dateEnd.getHours(dateEnd),
            dateEnd.getMinutes(dateEnd),
            dateEnd.getSeconds(dateEnd)
          );
          chartDataSeries['color'] = COLORS.EVENTO_CLIENTE;
          chartDataSeries['zIndex'] = 1;
        }
        if (item.id_tipo_marcado === 10) {
          let dateStart = new Date(item.fecha_inicio);
          let dateEnd = new Date(item.fecha_termino);
          chartDataSeries['from'] = Date.UTC(
            dateStart.getFullYear(dateStart),
            dateStart.getMonth(dateStart),
            dateStart.getDate(dateStart),
            dateStart.getHours(dateStart),
            dateStart.getMinutes(dateStart),
            dateStart.getSeconds(dateStart)
          );
          chartDataSeries['to'] = Date.UTC(
            dateEnd.getFullYear(dateEnd),
            dateEnd.getMonth(dateEnd),
            dateEnd.getDate(dateEnd),
            dateEnd.getHours(dateEnd),
            dateEnd.getMinutes(dateEnd),
            dateEnd.getSeconds(dateEnd)
          );
          chartDataSeries['color'] = COLORS.MARCADO_ATENTUS;
          chartDataSeries['zIndex'] = 1;
        }
        return chartDataSeries;
      }
    );

    return {
      chart: {
        type: 'spline',
        zoomType: 'x',
        ignoreHiddenSeries: false,
        marginRight: 30,
        resetZoomButton: {
          theme: {
            fill: 'white',
            stroke: 'silver',
            r: 0,
            states: {
              hover: {
                fill: '#41739D',
                style: {
                  color: 'white',
                },
              },
            },
          },
        },
      },
      title: null,
      subtitle: null,
      setOptions: {
        time: {
          useUTC: false,
        },
        lang: {
          months: t('months', { returnObjects: true }),
          weekdays: t('weekDays', { returnObjects: true }),
          locale: 'pt-PT',
        },
      },
      plotOptions: {
        series: {
          turboThreshold: 20000,
          gapSize: 2,
          marker: {
            symbol: 'circle',
            width: 8,
            height: 8,
            enabled: false,
          },
          point: {
            events: {
              mouseOver: function (event) {
                onHoverData(this);
              },
            },
          },
        },
      },
      legend: {
        enabled: true,
        useHTML: true,
        symbolWidth: 0,
        labelFormatter: function () {
          let symbol =
            '<div style="background:' +
            this.color +
            ';width:15px;height:3px;margin-top:5px"></div>';
          let name =
            '<button style="padding-left:4px;border-width:0px;background:white">' +
            this.name +
            '</button>';
          return (
            '<div style="display: flex;flex-direction: row;flex-wrap: nowrap;justify-content: space-between;">' +
            symbol +
            ' ' +
            name +
            '</div>'
          );
        },
      },
      navigator: {
        enabled: null,
      },
      tooltip: {
        enabled: false,
        pointFormat:
          t('response-time-step.graph.tooltip.point-format') +
          ': <b>{point.y:.2f} (s)</b>',
      },
      credits: {
        enabled: false,
      },
      xAxis: {
        lineColor: '#c0c0c0',
        type: 'datetime',
        minorGridLineColor: '#E0E0E0',
        minorTickLength: 0,
        gridLineWidth: 0.3,
        showFirstLabel: true,
        dateTimeLabelFormats: {
          second: '%l:%M:%S%P',
          minute: '%l:%M%P',
          hour: '%H:%M',
          day: '<br/><b>%e/%b</b>',
          week: '%e. %b',
          month: '%b %y',
          year: '%Y',
        },
        labels: {
          useHTML: true,
        },
        plotBands: arrayChartDataMarked,
      },
      yAxis: {
        min: 0,
        title: {
          text: t('response-time-general.graph.title'),
        },
        plotLines: [
          {
            value: sla_rendimiento_ok,
            color: '#54a51c',
            width: 2,
            zIndex: 3,
          },
          {
            value: sla_rendimiento_error,
            color: '#D22129',
            width: 2,
            zIndex: 3,
          },
        ],
      },
      navigator: {
        enabled: false,
      },
      series: arrayChartData,
    };
  }

  if (!externalData || Object.keys(externalData).length === 0) {
    return <Alert severity="info">{t('objective-not-data')}</Alert>;
  }

  const chartOptionsStackedBarConsolidated = processData(externalData);

  return (
    <Box ref={containerRef} width="100%">
      <HighchartsReact
        highcharts={Highcharts}
        options={chartOptionsStackedBarConsolidated}
        ref={chartResponseTimeConsolidatedRef}
        containerProps={{ style: { width: '100%' } }}
      />
      <GraphResponseTimeInfo {...hoverData} />
    </Box>
  );
}
